// src/pages/Support.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./LegalPages.css";

const MAX_LEN = 4000;

export default function Support() {
  const { currentUser } = useAuth();
  const isAuthed = !!(currentUser && (currentUser.uid || currentUser._source === "cookie"));

  const functions = useMemo(() => getFunctions(), []);
  const submitSupportRequest = useMemo(
    () => httpsCallable(functions, "submitSupportRequest"),
    [functions]
  );

  const [category, setCategory] = useState("general");
  const [priority, setPriority] = useState("normal"); // normal | urgent
  const [tradeId, setTradeId] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const remaining = MAX_LEN - (message?.length || 0);

  const canSubmit =
    isAuthed &&
    subject.trim().length >= 3 &&
    message.trim().length >= 10 &&
    message.length <= MAX_LEN &&
    !sending;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");

    if (!canSubmit) {
      setErr("Please complete the form before submitting.");
      return;
    }

    setSending(true);
    try {
      const payload = {
        category,
        priority,
        tradeId: tradeId.trim() || null,
        subject: subject.trim(),
        message: message.trim(),
        // No contact fields (name/phone/email) from UI.
      };

      const res = await submitSupportRequest(payload);
      const id = res?.data?.requestId;

      setOk(id ? `Request submitted. Reference: ${id}` : "Request submitted.");

      // reset form
      setCategory("general");
      setPriority("normal");
      setTradeId("");
      setSubject("");
      setMessage("");
    } catch (e2) {
      setErr(e2?.message || "Could not submit your request right now. Please try again shortly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="legal-page">
      <div className="legal-hero">
        <h1>Contact Support</h1>
        <p>Send a message to NexterZone Support. We do not display contact details between users.</p>

        <div className="legal-hero-links">
          <Link to="/help">Help Center</Link>
          <Link to="/faq">FAQ</Link>
          <Link to="/trust-safety">Trust &amp; Safety</Link>
        </div>
      </div>

      {!isAuthed ? (
        <div className="legal-callout">
          <h3>Sign in required</h3>
          <p>
            To protect the platform from abuse, support requests can only be sent from an authenticated account.
          </p>
          <div className="legal-actions">
            <Link className="legal-btn primary" to="/login" state={{ from: "/support" }}>
              Sign in
            </Link>
            <Link className="legal-btn" to="/help">
              Back to Help Center
            </Link>
          </div>
        </div>
      ) : (
        <div className="legal-grid">
          <section className="legal-card" style={{ gridColumn: "span 12" }}>
            <h2>Submit a request</h2>
            <p className="legal-muted" style={{ marginTop: 0 }}>
              For emergencies or immediate danger, contact local authorities first.
            </p>

            <form onSubmit={onSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 12 }}>
                {/* Row 1: Category + Priority */}
                <div style={{ gridColumn: "span 6" }}>
                  <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #d1d5db" }}
                  >
                    <option value="general">General</option>
                    <option value="trade">Trade issue</option>
                    <option value="account">Account issue</option>
                    <option value="safety">Safety concern</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{ gridColumn: "span 6" }}>
                  <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                    Priority
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #d1d5db" }}
                  >
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent (safety-related)</option>
                  </select>
                </div>

                {/* Row 2: Trade ID full width */}
                <div style={{ gridColumn: "span 12" }}>
                  <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                    Trade ID (optional)
                  </label>
                  <input
                    value={tradeId}
                    onChange={(e) => setTradeId(e.target.value)}
                    placeholder="Paste trade ID if applicable"
                    style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #d1d5db" }}
                    maxLength={120}
                  />
                </div>

                {/* Row 3: Subject */}
                <div style={{ gridColumn: "span 12" }}>
                  <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                    Subject
                  </label>
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Short summary of your issue"
                    style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #d1d5db" }}
                    maxLength={140}
                  />
                </div>

                {/* Row 4: Message */}
                <div style={{ gridColumn: "span 12" }}>
                  <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                    Message
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe what happened, what you expected, and what you see now."
                    style={{
                      width: "100%",
                      padding: 10,
                      borderRadius: 10,
                      border: "1px solid #d1d5db",
                      minHeight: 150,
                    }}
                    maxLength={MAX_LEN}
                  />
                  <div className="legal-muted" style={{ marginTop: 6 }}>
                    {remaining} characters remaining
                  </div>
                  <div className="legal-muted" style={{ marginTop: 6 }}>
                    Please do not include phone numbers, addresses, or personal contact details.
                  </div>
                </div>
              </div>

              {err ? (
                <div className="legal-callout">
                  <h3>Could not submit</h3>
                  <p>{err}</p>
                </div>
              ) : null}

              {ok ? (
                <div className="legal-callout">
                  <h3>Submitted</h3>
                  <p>{ok}</p>
                </div>
              ) : null}

              <div className="legal-actions">
                <button
                  type="submit"
                  className="legal-btn primary"
                  disabled={!canSubmit}
                  style={{ cursor: canSubmit ? "pointer" : "not-allowed" }}
                >
                  {sending ? "Submitting..." : "Submit request"}
                </button>
                <Link className="legal-btn" to="/help">
                  Back to Help Center
                </Link>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
